import { useSyncExternalStore } from 'react'

/**
 * Bridge store between the Workbench chart and the AI chat (both live under
 * BotDetail but in separate subtrees). Tracks edits since the last push
 * (the amber badge), the chart context staged on the chat composer, and the
 * commit that happens when the user sends the message. Module-level store +
 * useSyncExternalStore — the app has no state library, and this mirrors the
 * prototype's pushStore the flow was designed around.
 */

export interface StagedChart {
  label: string
  text: string
  /** Context items keyed by stable id — becomes aiPush.sentItems on commit. */
  items: Record<string, string>
  /** The symbol the context was built from — chart state and AI bookkeeping
   * are per-symbol, so the commit must fold into THAT symbol's diff base. */
  symbol: string
  /** Local wall-clock label shown on the chip ("2:32 PM"). */
  atLabel: string
  /** Dirty count absorbed by this stage — restored if the chip is removed. */
  changes: number
}

export interface ChartPushState {
  bid: string | null
  /** Chart edits since the last push (the badge). */
  dirtyCount: number
  /** Context sitting on the chat composer, sent with the next message. */
  staged: StagedChart | null
  /** Commits this session (>0 ⇒ "has pushed"); seeded from persisted state. */
  pushCount: number
  /** Last commit — lets the Workbench reconcile a send that happened while it was unmounted. */
  lastCommit: { sentAt: number; sentItems: Record<string, string>; symbol: string } | null
  /** True once the Workbench seeded counts from the persisted aiPush state. */
  seeded: boolean
}

const fresh = (bid: string | null): ChartPushState => ({
  bid,
  dirtyCount: 0,
  staged: null,
  pushCount: 0,
  lastCommit: null,
  seeded: false,
})

let state: ChartPushState = fresh(null)
const listeners = new Set<(s: ChartPushState) => void>()
const stageListeners = new Set<(staged: StagedChart) => void>()
// Stable per-bid empty snapshot, so the hook's getSnapshot stays pure and
// referentially stable while the store still holds another bot's state.
let freshSnap: ChartPushState = fresh(null)

function emit() {
  listeners.forEach((fn) => fn(state))
}

/** Reset to a clean slate when a different bot's workbench/chat touches the store. */
function ensure(bid: string) {
  if (state.bid !== bid) {
    state = fresh(bid)
    emit()
  }
}

/** Pure read for render paths: never mutates, never emits. */
function snapshotFor(bid: string): ChartPushState {
  if (state.bid === bid) return state
  if (freshSnap.bid !== bid) freshSnap = fresh(bid)
  return freshSnap
}

export const chartPush = {
  /** Read for event handlers — adopts the bid (resets if it changed). */
  get(bid: string): ChartPushState {
    ensure(bid)
    return state
  },

  ensure,

  subscribe(fn: (s: ChartPushState) => void): () => void {
    listeners.add(fn)
    return () => listeners.delete(fn)
  },

  /** Fired on stage() — drives the button→composer fly animation. */
  onStage(fn: (staged: StagedChart) => void): () => void {
    stageListeners.add(fn)
    return () => stageListeners.delete(fn)
  },

  /** Seed counts (and a persisted staged chip, so "Attached" survives a
   * reload) from the persisted state; only when nothing happened yet. */
  seedIfFresh(bid: string, seed: { dirtyCount: number; hasPushes: boolean; staged?: StagedChart | null }) {
    ensure(bid)
    if (state.seeded || state.dirtyCount > 0 || state.staged || state.pushCount > 0) return
    state = {
      ...state,
      // A restored chip has already absorbed the badge (its own `changes`
      // field restores it if the chip is removed).
      dirtyCount: seed.staged ? 0 : seed.dirtyCount,
      staged: seed.staged ?? null,
      pushCount: seed.hasPushes ? 1 : 0,
      seeded: true,
    }
    emit()
  },

  markDirty(bid: string) {
    ensure(bid)
    state = { ...state, dirtyCount: state.dirtyCount + 1 }
    emit()
  },

  /** Absolute badge value computed by the Workbench (diff vs last-sent items). */
  setDirty(bid: string, n: number) {
    ensure(bid)
    const v = Math.max(0, Math.floor(n))
    if (state.dirtyCount === v) return
    state = { ...state, dirtyCount: v }
    emit()
  },

  /** Badge turned out to be stale (nothing actually differs) — zero it. */
  clearDirty(bid: string) {
    ensure(bid)
    if (state.dirtyCount === 0) return
    state = { ...state, dirtyCount: 0 }
    emit()
  },

  /** Put the chart context on the composer; re-staging refreshes it in place. */
  stage(bid: string, input: { label: string; text: string; items: Record<string, string>; symbol: string; atLabel: string }): StagedChart {
    ensure(bid)
    // Accumulate absorbed changes across re-stages so removing the chip
    // restores the full pre-attach badge, not just the latest delta. A
    // re-stage from a DIFFERENT symbol replaces the chip outright — the old
    // symbol's absorbed count belongs to its own slice, whose badge is
    // recomputed from the diff base when that symbol is activated again.
    const carried = state.staged && state.staged.symbol === input.symbol ? state.staged.changes : 0
    const staged: StagedChart = { ...input, changes: carried + state.dirtyCount }
    state = { ...state, staged, dirtyCount: 0 }
    emit()
    stageListeners.forEach((fn) => fn(staged))
    return staged
  },

  /** User edited the staged context text in the review dialog. */
  updateStagedText(bid: string, text: string) {
    ensure(bid)
    if (!state.staged) return
    state = { ...state, staged: { ...state.staged, text } }
    emit()
  },

  /** Chip removed without sending — restore the badge exactly as before.
   * If the workbench switched to another symbol since the stage, the restored
   * count is momentarily the old symbol's; the Workbench recounts on every
   * unstage and corrects the active badge within its debounce (~300ms). */
  clearStaged(bid: string) {
    ensure(bid)
    if (!state.staged) return
    state = { ...state, dirtyCount: state.staged.changes, staged: null }
    emit()
  },

  /** The message carrying the staged context was sent successfully. */
  commit(bid: string): StagedChart | null {
    ensure(bid)
    const staged = state.staged
    if (!staged) return null
    state = {
      ...state,
      staged: null,
      pushCount: state.pushCount + 1,
      lastCommit: { sentAt: Date.now(), sentItems: staged.items, symbol: staged.symbol },
    }
    emit()
    return staged
  },

  reset(bid: string) {
    state = fresh(bid)
    emit()
  },
}

export function useChartPush(bid: string): ChartPushState {
  return useSyncExternalStore(chartPush.subscribe, () => snapshotFor(bid))
}
